import { useCallback, useEffect, useRef, useState } from 'react'
import {
    ArrowUp,
    Globe,
    Hash,
    Image as ImageIcon,
    Layout,
    Layers,
    Package,
    Sparkles,
    Store,
    User,
} from 'lucide-react'
import { motion } from 'motion/react'
import type { AdPlatform, MetaAd, Persona } from '@storefronts/common-types'
import { AD_PLATFORM, VARIANT_COUNT_OPTIONS } from '@storefronts/common-types'
import type { AdminLandingPageLink } from '@storefronts/common-types'
import { Button } from '@/components/ui/button'
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover'
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/ui/tooltip'
import { H1, Muted } from '@/components/ui/typography'
import { AnimatedGradientText } from '@/components/magicui/AnimatedGradientText'
import { DotPattern } from '@/components/magicui/DotPattern'
import { cn } from '@/lib/utils'
import { useAppShellVisible } from '@/components/shared/AppShellContext'
import { LandingPageLinksSelectionModal } from '@/components/modals/LandingPageLinkSelectionModal'
import type { ManualCreativePayload } from '../shared/useManualCreativeUploadForm'
import type { SearchQueryPayload } from '../shared/SearchQueryModal'
import {
    ChannelCreativePickerModal,
    type ChannelCreativeResult,
} from './ChannelCreativePickerModal'
import { PersonaPickerModal } from './PersonaPickerModal'
import { RemovablePill } from './PrefacePills'
import {
    TemplatePickerModal,
    type TemplatePickerResult,
} from './TemplatePickerModal'
import {
    MINIMUM_CREATE_CONTEXT_HINT,
    hasDeeplinkUrl,
    hasMinimumCreateContext,
    shouldShowContextHint,
    submitButtonTooltip,
} from './prefaceMinimumContext'

const CHANNEL_LABELS: Record<string, string> = {
    [AD_PLATFORM.META]: 'Meta',
    [AD_PLATFORM.GOOGLE]: 'Google',
    [AD_PLATFORM.TIKTOK]: 'TikTok',
    [AD_PLATFORM.AXON]: 'Axon',
    [AD_PLATFORM.PINTEREST]: 'Pinterest',
    [AD_PLATFORM.SNAPCHAT]: 'Snapchat',
    [AD_PLATFORM.ROKT]: 'Rokt',
    [AD_PLATFORM.UNSPECIFIED]: 'Other',
}

function channelLabel(platform: AdPlatform): string {
    return CHANNEL_LABELS[platform] ?? platform
}

function formatChannelPillLabel(
    channel: AdPlatform,
    ad: MetaAd | null,
    manualCreative: ManualCreativePayload | null,
    searchQuery: SearchQueryPayload | null,
): string {
    const base = channelLabel(channel)
    if (ad) return `${base} · ${ad.name}`
    if (searchQuery) return `${base} · ${searchQuery.searchQuery}`
    if (manualCreative) return `${base} · Manual creative`
    return base
}

/** Lightweight `{ id, name }` shape used for persona selections — the
 * composer only needs enough to render the chip. */
type NamedRef = { id: number; name: string }

// A reference URL and a source storefront are alternative layout inputs; the
// Layout pill reflects whichever is set.
function isLayoutChosen(
    referenceUrl: string | null,
    sourceStorefront: SourceStorefrontRef | null,
): boolean {
    return Boolean(referenceUrl || sourceStorefront)
}

function hasChannelCreative(
    ad: MetaAd | null,
    manualCreative: ManualCreativePayload | null,
    searchQuery: SearchQueryPayload | null,
): boolean {
    return Boolean(
        ad ||
            searchQuery ||
            manualCreative?.assets.length ||
            manualCreative?.adCopy?.trim(),
    )
}

function hasSelectionPills(
    channel: AdPlatform | null,
    products: AdminLandingPageLink[],
    persona: NamedRef | null,
    layoutChosen: boolean,
    variantCount: number,
): boolean {
    return Boolean(
        channel ||
            products.length ||
            persona ||
            layoutChosen ||
            variantCount > 1,
    )
}

function optionalHashes(
    products: AdminLandingPageLink[],
): string[] | undefined {
    return products.length > 0 ? products.map((p) => p.linkHash) : undefined
}

function optionalTitles(
    products: AdminLandingPageLink[],
): string[] | undefined {
    return products.length > 0 ? products.map((p) => p.title) : undefined
}

function buildPrefaceSubmission(input: {
    channel: AdPlatform | null
    ad: MetaAd | null
    manualCreative: ManualCreativePayload | null
    searchQuery: SearchQueryPayload | null
    userInput: string
    persona: NamedRef | null
    products: AdminLandingPageLink[]
    referenceUrl: string | null
    sourceStorefront: SourceStorefrontRef | null
    variantCount: number
}): PrefaceSubmission {
    return {
        channel: input.channel ?? undefined,
        ad: input.ad ?? undefined,
        manualCreative: input.manualCreative ?? undefined,
        searchQuery: input.searchQuery ?? undefined,
        userInput: input.userInput.trim() || undefined,
        personaId: input.persona?.id,
        personaName: input.persona?.name,
        productHashes: optionalHashes(input.products),
        productNames: optionalTitles(input.products),
        referenceUrl: input.referenceUrl ?? undefined,
        sourceStorefrontHash: input.sourceStorefront?.hash,
        sourceStorefrontName: input.sourceStorefront?.name,
        variantCount: input.variantCount,
    }
}

function layoutFromPickerResult(result: TemplatePickerResult): {
    referenceUrl: string | null
    referenceTitle: string | null
    sourceStorefront: SourceStorefrontRef | null
} {
    return {
        referenceUrl: result.kind === 'referenceUrl' ? result.url : null,
        referenceTitle:
            result.kind === 'referenceUrl' ? (result.title ?? null) : null,
        sourceStorefront:
            result.kind === 'sourceStorefront'
                ? { hash: result.landingPageHash, name: result.name }
                : null,
    }
}

function channelForInitialAd(
    ad: MetaAd | null,
    channel: AdPlatform,
): AdPlatform | null {
    return ad ? channel : null
}

export interface PrefaceSubmission {
    channel?: AdPlatform
    ad?: MetaAd
    manualCreative?: ManualCreativePayload
    searchQuery?: SearchQueryPayload
    userInput?: string
    personaId?: number
    personaName?: string
    productHashes?: string[]
    productNames?: string[]
    referenceUrl?: string
    sourceStorefrontHash?: string
    sourceStorefrontName?: string
    variantCount?: number
}

/** The hash is the selection; the name is a label that may arrive later. */
export type SourceStorefrontRef = { hash: string; name?: string }

/** Enough of the hash to tell two storefronts apart before the name lands. */
export function sourceStorefrontLabel(ref: SourceStorefrontRef): string {
    return ref.name || ref.hash.slice(0, 8)
}

/**
 * The composer's storefront selection, which a "Create Variant" deep link seeds.
 *
 * The seed changes twice after first render: it is empty until the router
 * hydrates the query, then gains the storefront's name when the lookup lands.
 * Latching it into state at mount would freeze whichever of those the composer
 * happened to catch, so state holds only what the *user* did — undefined while
 * they have not touched the layout picker, null once they clear it — and the
 * live seed shows through until then. A pick or a clear wins from then on.
 */
function useSourceStorefrontSelection(
    seed: SourceStorefrontRef | null,
): [SourceStorefrontRef | null, (picked: SourceStorefrontRef | null) => void] {
    const [picked, setPicked] = useState<SourceStorefrontRef | null>()
    return [picked === undefined ? seed : picked, setPicked]
}

interface PrefaceComposerProps {
    onSubmit: (submission: PrefaceSubmission) => void
    /** Storefront preselected as the layout, e.g. from "Create Variant". */
    initialSourceStorefront: SourceStorefrontRef | null
    /** Set by the parent while the create-session request is in flight so the
     * send button shows a loading state instead of the arrow. */
    isSubmitting?: boolean
    initialAd: MetaAd | null
    initialAdChannel: AdPlatform
}

export function PrefaceComposer({
    onSubmit,
    initialSourceStorefront,
    isSubmitting = false,
    initialAd,
    initialAdChannel,
}: PrefaceComposerProps) {
    const textareaRef = useRef<HTMLTextAreaElement>(null)
    const isShellVisible = useAppShellVisible()

    useEffect(() => {
        if (!isShellVisible) return
        textareaRef.current?.focus({ preventScroll: true })
    }, [isShellVisible])

    const [userInput, setUserInput] = useState('')

    const [channel, setChannel] = useState<AdPlatform | null>(() =>
        channelForInitialAd(initialAd, initialAdChannel),
    )
    const [ad, setAd] = useState<MetaAd | null>(initialAd)
    const [manualCreative, setManualCreative] =
        useState<ManualCreativePayload | null>(null)
    const [searchQuery, setSearchQuery] = useState<SearchQueryPayload | null>(
        null,
    )

    const [persona, setPersona] = useState<NamedRef | null>(null)
    const [products, setProducts] = useState<AdminLandingPageLink[]>([])
    const [referenceUrl, setReferenceUrl] = useState<string | null>(null)
    const [referenceTitle, setReferenceTitle] = useState<string | null>(null)
    const [sourceStorefront, setSourceStorefront] =
        useSourceStorefrontSelection(initialSourceStorefront)
    const [variantCount, setVariantCount] = useState<number>(1)

    const [channelPickerOpen, setChannelPickerOpen] = useState(false)
    const [personaPickerOpen, setPersonaPickerOpen] = useState(false)
    const [productPickerOpen, setProductPickerOpen] = useState(false)
    const [templatePickerOpen, setTemplatePickerOpen] = useState(false)
    const [variantPopoverOpen, setVariantPopoverOpen] = useState(false)

    const hasMinimumContext = hasMinimumCreateContext({
        hasCreative: hasChannelCreative(ad, manualCreative, searchQuery),
        hasDeeplinkUrl: hasDeeplinkUrl(
            referenceUrl,
            manualCreative?.productUrl,
        ),
        hasProducts: products.length > 0,
    })
    const canSubmit = hasMinimumContext && !isSubmitting

    const handleChannelCreativeSelected = useCallback(
        (result: ChannelCreativeResult) => {
            setChannel(result.channel)
            // Selecting a channel resets prior creatives so we never keep a
            // stale Meta ad after the user switches to a manual upload (or
            // vice versa).
            setAd(result.ad ?? null)
            setManualCreative(result.manualCreative ?? null)
            setSearchQuery(result.searchQuery ?? null)
        },
        [],
    )

    const handlePersonaSelected = useCallback((p: Persona) => {
        setPersona({ id: p.id, name: p.name })
    }, [])

    const handleProductsSaved = useCallback(
        async (links: AdminLandingPageLink[]) => {
            setProducts(links)
        },
        [],
    )

    const removeProduct = useCallback((shopifyId: string) => {
        setProducts((prev) => prev.filter((p) => p.shopifyId !== shopifyId))
    }, [])

    const handleTemplateSelected = useCallback(
        (result: TemplatePickerResult) => {
            const next = layoutFromPickerResult(result)
            setReferenceUrl(next.referenceUrl)
            setReferenceTitle(next.referenceTitle)
            setSourceStorefront(next.sourceStorefront)
        },
        [setSourceStorefront],
    )

    const handleSubmit = useCallback(() => {
        if (!canSubmit) return
        onSubmit(
            buildPrefaceSubmission({
                channel,
                ad,
                manualCreative,
                searchQuery,
                userInput,
                persona,
                products,
                referenceUrl,
                sourceStorefront,
                variantCount,
            }),
        )
    }, [
        canSubmit,
        channel,
        ad,
        manualCreative,
        searchQuery,
        userInput,
        persona,
        products,
        referenceUrl,
        sourceStorefront,
        variantCount,
        onSubmit,
    ])

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
            if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                handleSubmit()
            }
        },
        [handleSubmit],
    )

    const layoutChosen = isLayoutChosen(referenceUrl, sourceStorefront)

    const hasPills = hasSelectionPills(
        channel,
        products,
        persona,
        layoutChosen,
        variantCount,
    )

    return (
        <TooltipProvider delayDuration={150}>
            <div className="relative flex h-full items-center justify-center overflow-hidden px-6 py-10">
                <DotPattern className="opacity-60 [mask-image:radial-gradient(440px_circle_at_center,white,transparent)]" />
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, ease: 'easeOut' }}
                    className="relative w-full max-w-2xl"
                >
                    <div className="mb-6 text-center">
                        <H1 className="text-3xl font-semibold tracking-tight">
                            <AnimatedGradientText
                                colorFrom="#065F46"
                                colorTo="#22C55E"
                            >
                                Optimize after the click
                            </AnimatedGradientText>
                        </H1>
                        <Muted className="mx-auto mt-3 block max-w-md text-base">
                            Add a creative, a destination URL, or products.
                            Channel, persona, and layout are optional &ndash;
                            I&apos;ll fill in whatever you skip.
                        </Muted>
                    </div>

                    <div className="focus-within:border-foreground/20 rounded-xl border shadow-sm transition-all bg-background border-border focus-within:shadow-md">
                        {hasPills && (
                            <SelectionPillRow
                                channel={channel}
                                ad={ad}
                                manualCreative={manualCreative}
                                searchQuery={searchQuery}
                                persona={persona}
                                products={products}
                                referenceUrl={referenceUrl}
                                referenceTitle={referenceTitle}
                                sourceStorefront={sourceStorefront}
                                variantCount={variantCount}
                                onClearChannel={() => {
                                    setChannel(null)
                                    setAd(null)
                                    setManualCreative(null)
                                    setSearchQuery(null)
                                }}
                                onClearPersona={() => setPersona(null)}
                                onRemoveProduct={removeProduct}
                                onClearReferenceUrl={() => {
                                    setReferenceUrl(null)
                                    setReferenceTitle(null)
                                }}
                                onClearSourceStorefront={() =>
                                    setSourceStorefront(null)
                                }
                                onResetVariantCount={() => setVariantCount(1)}
                            />
                        )}

                        <textarea
                            ref={textareaRef}
                            value={userInput}
                            onChange={(e) => setUserInput(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="Describe what you want to build, or leave blank and I'll infer from the creative, URL, or products..."
                            rows={2}
                            className="max-h-32 min-h-12 w-full resize-none overflow-y-auto px-3 py-2 text-sm leading-snug outline-none bg-transparent placeholder:text-muted-foreground"
                        />

                        <div className="flex items-center justify-between gap-1.5 px-1.5 pb-1.5">
                            <div className="flex items-center gap-1.5">
                                <ToolbarPill
                                    icon={<ImageIcon size={14} />}
                                    label="Channel & creative"
                                    active={!!channel}
                                    onClick={() => setChannelPickerOpen(true)}
                                    tooltip="Add a creative"
                                />
                                <ToolbarPill
                                    icon={<User size={14} />}
                                    label="Persona"
                                    active={!!persona}
                                    onClick={() => setPersonaPickerOpen(true)}
                                    tooltip="Pick a persona"
                                />
                                <ToolbarPill
                                    icon={<Package size={14} />}
                                    label="Products"
                                    active={products.length > 0}
                                    onClick={() => setProductPickerOpen(true)}
                                    tooltip="Add products"
                                />
                                <ToolbarPill
                                    icon={<Layout size={14} />}
                                    label="Layout"
                                    active={layoutChosen}
                                    onClick={() => setTemplatePickerOpen(true)}
                                    tooltip="Add an inspiration page, URL, or storefront"
                                />
                            </div>

                            <PrefaceComposerActions
                                variantCount={variantCount}
                                variantPopoverOpen={variantPopoverOpen}
                                onVariantPopoverOpenChange={
                                    setVariantPopoverOpen
                                }
                                onVariantCountChange={setVariantCount}
                                canSubmit={canSubmit}
                                isSubmitting={isSubmitting}
                                onSubmit={handleSubmit}
                            />
                        </div>
                    </div>
                    {shouldShowContextHint(hasMinimumContext, isSubmitting) && (
                        <Muted className="mt-3 block text-center text-xs">
                            {MINIMUM_CREATE_CONTEXT_HINT}
                        </Muted>
                    )}
                </motion.div>

                <ChannelCreativePickerModal
                    open={channelPickerOpen}
                    onOpenChange={setChannelPickerOpen}
                    onSelected={handleChannelCreativeSelected}
                />

                <PersonaPickerModal
                    open={personaPickerOpen}
                    onOpenChange={setPersonaPickerOpen}
                    onSelected={handlePersonaSelected}
                    currentPersonaId={persona?.id}
                />

                {productPickerOpen && (
                    <LandingPageLinksSelectionModal
                        isOpen
                        onClose={() => setProductPickerOpen(false)}
                        defaultSelectedLinks={products}
                        onSave={handleProductsSaved}
                        modalTitle="Select products"
                        showFeatureBadge={false}
                    />
                )}

                <TemplatePickerModal
                    open={templatePickerOpen}
                    onOpenChange={setTemplatePickerOpen}
                    onSelected={handleTemplateSelected}
                    currentReferenceUrl={referenceUrl ?? undefined}
                    currentSourceStorefrontHash={sourceStorefront?.hash}
                />
            </div>
        </TooltipProvider>
    )
}

function PrefaceComposerActions({
    variantCount,
    variantPopoverOpen,
    onVariantPopoverOpenChange,
    onVariantCountChange,
    canSubmit,
    isSubmitting,
    onSubmit,
}: {
    variantCount: number
    variantPopoverOpen: boolean
    onVariantPopoverOpenChange: (open: boolean) => void
    onVariantCountChange: (count: number) => void
    canSubmit: boolean
    isSubmitting: boolean
    onSubmit: () => void
}) {
    const variantTooltip =
        variantCount > 1
            ? `${variantCount} variants — click to change`
            : '1 variant — click to change'

    return (
        <div className="flex items-center gap-1.5">
            <Popover
                open={variantPopoverOpen}
                onOpenChange={onVariantPopoverOpenChange}
            >
                <Tooltip>
                    <TooltipTrigger asChild>
                        <PopoverTrigger asChild>
                            <span>
                                <ToolbarPill
                                    icon={<Layers size={14} />}
                                    label={String(variantCount)}
                                />
                            </span>
                        </PopoverTrigger>
                    </TooltipTrigger>
                    <TooltipContent side="top">{variantTooltip}</TooltipContent>
                </Tooltip>
                <PopoverContent side="top" align="end" className="w-auto p-2">
                    <div className="flex gap-1.5">
                        {VARIANT_COUNT_OPTIONS.map((n) => (
                            <button
                                key={n}
                                type="button"
                                className={cn(
                                    'flex size-9 items-center justify-center rounded-md border text-sm font-medium transition-colors',
                                    variantCount === n
                                        ? 'border-foreground/40 bg-muted text-foreground'
                                        : 'border-border hover:bg-muted/50 text-muted-foreground',
                                )}
                                onClick={() => {
                                    onVariantCountChange(n)
                                    onVariantPopoverOpenChange(false)
                                }}
                            >
                                {n}
                            </button>
                        ))}
                    </div>
                </PopoverContent>
            </Popover>
            <Tooltip>
                <TooltipTrigger asChild>
                    <span>
                        <Button
                            type="button"
                            variant="blackcrow"
                            size="icon"
                            aria-label="Start build"
                            disabled={!canSubmit}
                            className="size-7 rounded-full"
                            onClick={onSubmit}
                        >
                            {isSubmitting ? (
                                <Sparkles size={14} className="animate-pulse" />
                            ) : (
                                <ArrowUp size={14} />
                            )}
                        </Button>
                    </span>
                </TooltipTrigger>
                <TooltipContent side="top">
                    {submitButtonTooltip(isSubmitting, canSubmit)}
                </TooltipContent>
            </Tooltip>
        </div>
    )
}

function ToolbarPill({
    icon,
    label,
    badge,
    active,
    onClick,
    tooltip,
}: {
    icon: React.ReactNode
    label?: string
    badge?: string
    active?: boolean
    onClick?: () => void
    tooltip?: string
}) {
    const button = (
        <button
            type="button"
            className={cn(
                'inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
                active
                    ? 'border-foreground/20 bg-muted text-foreground'
                    : 'border-border text-muted-foreground hover:border-foreground/20 hover:text-foreground',
            )}
            onMouseDown={(e) => e.preventDefault()}
            onClick={onClick}
        >
            {icon}
            {label && <span>{label}</span>}
            {badge && (
                <span className="rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase leading-none tracking-wide bg-muted text-muted-foreground">
                    {badge}
                </span>
            )}
        </button>
    )

    if (!tooltip) return button
    return (
        <Tooltip>
            <TooltipTrigger asChild>
                <span>{button}</span>
            </TooltipTrigger>
            <TooltipContent side="top">{tooltip}</TooltipContent>
        </Tooltip>
    )
}

function SelectionPillRow({
    channel,
    ad,
    manualCreative,
    searchQuery,
    persona,
    products,
    referenceUrl,
    referenceTitle,
    sourceStorefront,
    variantCount,
    onClearChannel,
    onClearPersona,
    onRemoveProduct,
    onClearReferenceUrl,
    onClearSourceStorefront,
    onResetVariantCount,
}: {
    channel: AdPlatform | null
    ad: MetaAd | null
    manualCreative: ManualCreativePayload | null
    searchQuery: SearchQueryPayload | null
    persona: NamedRef | null
    products: AdminLandingPageLink[]
    referenceUrl: string | null
    referenceTitle: string | null
    sourceStorefront: SourceStorefrontRef | null
    variantCount: number
    onClearChannel: () => void
    onClearPersona: () => void
    onRemoveProduct: (shopifyId: string) => void
    onClearReferenceUrl: () => void
    onClearSourceStorefront: () => void
    onResetVariantCount: () => void
}) {
    return (
        <div className="border-border/60 flex flex-wrap items-center gap-1.5 border-b px-3 py-2">
            {channel && (
                <RemovablePill
                    imageUrl={ad?.thumbnailUrl}
                    imageAlt={ad?.name}
                    icon={
                        <Hash
                            size={11}
                            className="shrink-0 text-muted-foreground"
                        />
                    }
                    label={formatChannelPillLabel(
                        channel,
                        ad,
                        manualCreative,
                        searchQuery,
                    )}
                    ariaLabel="Remove channel"
                    onRemove={onClearChannel}
                />
            )}
            {persona && (
                <RemovablePill
                    icon={
                        <User
                            size={11}
                            className="shrink-0 text-muted-foreground"
                        />
                    }
                    label={persona.name}
                    ariaLabel="Remove persona"
                    onRemove={onClearPersona}
                />
            )}
            {products.map((product) => (
                <RemovablePill
                    key={product.shopifyId}
                    imageUrl={product.imageUrl}
                    imageAlt={product.title}
                    icon={
                        <Package
                            size={11}
                            className="shrink-0 text-muted-foreground"
                        />
                    }
                    label={product.title}
                    ariaLabel="Remove product"
                    onRemove={() => onRemoveProduct(product.shopifyId)}
                />
            ))}
            {referenceUrl && (
                <RemovablePill
                    icon={
                        <Globe
                            size={11}
                            className="shrink-0 text-muted-foreground"
                        />
                    }
                    label={referenceTitle ?? referenceUrl}
                    ariaLabel="Remove reference URL"
                    onRemove={onClearReferenceUrl}
                />
            )}
            {sourceStorefront && (
                <RemovablePill
                    icon={
                        <Store
                            size={11}
                            className="shrink-0 text-muted-foreground"
                        />
                    }
                    label={sourceStorefrontLabel(sourceStorefront)}
                    ariaLabel="Remove source storefront"
                    onRemove={onClearSourceStorefront}
                />
            )}
            {variantCount > 1 && (
                <RemovablePill
                    icon={
                        <Layers
                            size={11}
                            className="shrink-0 text-muted-foreground"
                        />
                    }
                    label={`${variantCount} variants`}
                    ariaLabel="Reset variant count"
                    onRemove={onResetVariantCount}
                />
            )}
        </div>
    )
}
